<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  values: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const rootEl = ref(null);

function onOutsideClick(e) {
  if (open.value && rootEl.value && !rootEl.value.contains(e.target)) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener('click', onOutsideClick));
onUnmounted(() => document.removeEventListener('click', onOutsideClick));

function optKey(opt) {
  return typeof opt === 'object' ? opt.key : opt;
}
function optLabel(opt) {
  return typeof opt === 'object' ? opt.label : opt;
}

const label = computed(() => {
  if (!props.modelValue.length) return '—';
  const labels = props.modelValue.map(key => {
    const found = props.values.find(o => optKey(o) === key);
    return found ? optLabel(found) : key;
  });
  // Monta a string completa com vírgulas; se não couber (~32 chars), trunca com +N
  const full = labels.join(', ');
  if (full.length <= 32) return full;
  let result = '';
  let shown = 0;
  for (const l of labels) {
    const next = result ? `${result}, ${l}` : l;
    if (next.length > 28) break;
    result = next;
    shown++;
  }
  const remaining = labels.length - shown;
  return remaining > 0 ? `${result} +${remaining}` : result;
});

function toggle(opt) {
  const key = optKey(opt);
  const current = props.modelValue ?? [];
  const next = current.includes(key)
    ? current.filter(v => v !== key)
    : [...current, key];
  emit('update:modelValue', next);
}

function clear() {
  emit('update:modelValue', []);
  open.value = false;
}
</script>

<template>
  <div ref="rootEl" class="relative">
    <button
      type="button"
      class="w-full flex items-center justify-between gap-1 text-sm text-left px-2 py-1 rounded bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 min-h-[30px] outline-none"
      :class="{ 'bg-n-alpha-2': open }"
      @click="open = !open"
    >
      {{ label }}
    </button>

    <div
      v-show="open"
      class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-full"
      @click.stop
    >
      <button
        v-for="opt in values"
        :key="optKey(opt)"
        type="button"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-12"
        :class="{
          'text-n-brand font-medium': modelValue?.includes(optKey(opt)),
        }"
        @click="toggle(opt)"
      >
        <span class="flex-1 text-left">{{ optLabel(opt) }}</span>
        <span
          v-if="modelValue?.includes(optKey(opt))"
          class="i-lucide-check size-3 text-n-brand"
        />
      </button>
      <div class="border-t border-n-weak mt-1 pt-1">
        <button
          type="button"
          class="w-full px-3 py-1.5 text-xs text-n-slate-10 hover:bg-n-alpha-1 transition-colors text-left"
          @click="clear"
        >
          Limpar
        </button>
      </div>
    </div>
  </div>
</template>
